export interface DireccionDetalles {
  placeId: string;
  direccionCompleta: string;
  direccionNormalizada: string;
  calle?: string;
  numero?: string;
  colonia?: string;
  ciudad?: string;
  estado?: string;
  codigoPostal?: string;
  pais: string;
  coords: {
    lat: number;
    lng: number;
  };
  addressComponents?: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}

export interface DireccionValidacion {
  isValid: boolean;
  error?: string;
  partialMatch?: boolean;
}

export interface AreaServicio {
  pais: string;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export interface Cliente {
  id?: string;
  nombre: string;
  telefono: string;
  direccion: string;
  direccionDetalles?: DireccionDetalles;
  coords?: {
    lat: number;
    lng: number;
  };
  frecuenciaVisita: 'semanal' | 'quincenal' | 'mensual';
  diaVisita: 'lunes' | 'martes' | 'miércoles' | 'jueves' | 'viernes' | 'sábado' | 'domingo';
  diasVisita?: ('lunes' | 'martes' | 'miércoles' | 'jueves' | 'viernes' | 'sábado' | 'domingo')[];
  observaciones?: string;
  bidones10?: number;
  bidones20?: number;
  sodas?: number;
  envasesDevueltos?: number;
  total?: number;
  pagado?: boolean;
  saldoPendiente?: number;
  createdAt?: Date;
  updatedAt?: Date;
  ultimaEntregaFecha?: Date;
  consumoPromedioBidones?: number;
  consumoPromedioSodas?: number;
  zona?: string;
  
  // Control de envases
  balanceEnvases?: {
    envasesEntregados: number;
    envasesDevueltos: number;
    balanceActual: number;
    ultimaActualizacion: Date;
  };
  
  configuracionEnvases?: {
    alertaBalanceNegativo: boolean;
    umbralAlerta: number; // -5, -10, etc.
    notificacionesActivas: boolean;
  };
}

export interface ProductoEntrega {
  productoId: string;
  nombre: string;
  cantidad: number;
  precio: number;
  subtotal: number;
}

export interface Entrega {
  id: string;
  clienteId: string;
  fecha: Date;
  // Campos legacy para compatibilidad
  sodas?: number;
  bidones10?: number;
  bidones20?: number;
  // Nuevo campo para productos dinámicos
  productos?: ProductoEntrega[];
  // Envases entregados (llenos) por tipo
  envasesEntregados?: {
    [envaseTipoId: string]: {
      envaseTipoId: string;
      envaseTipoNombre: string;
      cantidad: number;
    };
  };
  // Campo legacy para compatibilidad
  envasesDevueltos?: number;
  
  // Envases devueltos (vacíos) por tipo
  envasesDevueltosPorTipo?: {
    [envaseTipoId: string]: {
      envaseTipoId: string;
      envaseTipoNombre: string;
      cantidad: number;
    };
  };
  // Gestión de comodato
  comodatoAsignaciones?: Array<{
    productoId: string;
    envaseTipoId: string;
    cantidad: number;
  }>;
  comodatoDevoluciones?: Array<{
    envaseTipoId: string;
    cantidad: number;
  }>;
  // Gestión de equipos
  equipos?: Array<{
    equipoId: string;
    tipo: TipoEquipo;
    marca: string;
    modelo: string;
    numeroSerie: string;
    accion: 'instalar' | 'retirar' | 'mantenimiento';
    observaciones?: string;
    costo?: number;
    ubicacion?: string;
    fechaAccion?: Date;
  }>;
  total: number;
  pagado: boolean;
  tipoPago?: 'no_pagado' | 'pagado_completo' | 'pago_entrega_actual' | 'pago_parcial';
  montoPagado?: number;
  medioPago?: 'efectivo' | 'transferencia' | 'tarjeta';
  firmaURL?: string;
  fotoEntregaURL?: string;
  observaciones?: string;
  createdAt: Date;
  _hasPendingWrites?: boolean;
  anulado?: boolean;
}

export interface Pago {
  id: string;
  clienteId: string;
  fecha: Date;
  monto: number;
  medioPago: 'efectivo' | 'transferencia' | 'tarjeta';
  nota?: string;
  createdAt: Date;
}

export interface InventarioVehiculo {
  id: string;
  fecha: Date;
  sodas: number;
  bidones10: number;
  bidones20: number;
  envasesDevueltos: number;
  updatedAt?: Date;
}

// ============================================
// SISTEMA DE GESTIÓN DE ENVASES - ELIMINADO
// ============================================
// Los envases ahora se manejan como propiedades de productos
// Ver: envaseRetornable en interface Producto
// Ver: comodatoTypes.ts para sistema de comodato

// Interfaces de envases legacy eliminadas
// Los envases ahora se manejan como propiedades de productos

export interface User {
  id?: string;
  uid: string;
  nombre: string;
  email: string;
  rol: 'owner' | 'admin' | 'manager' | 'sodero'; // Agregamos 'owner' como rol principal
  plan: 'individual' | 'business' | 'enterprise';
  tenantId: string;
  createdAt: Date;
  updatedAt?: Date;
  isActive?: boolean; // Para empleados activos/inactivos
  invitedBy?: string; // UID del usuario que invitó
  invitedAt?: Date; // Fecha de invitación
  acceptedAt?: Date; // Fecha de aceptación de la invitación
  // Campos adicionales del perfil
  telefono?: string;
  empresa?: string;
  direccion?: string;
  // Autenticación de dos factores
  twoFactor?: {
    isEnabled: boolean;
    secret?: string;
    backupCodes?: string[];
    createdAt?: Date;
    updatedAt?: Date;
  };
}

export interface Tenant {
  id: string;
  nombre: string;
  email: string;
  plan: 'individual' | 'business' | 'enterprise';
  maxUsers: number; // 1, 11, o null (ilimitado)
  currentUserCount: number; // Contador actual de usuarios
  adminUid: string; // UID del owner
  empleados: {
    uid: string;
    rol: 'admin' | 'manager' | 'sodero';
    nombre: string;
    email: string;
    isActive: boolean;
    invitedAt: Date;
    acceptedAt?: Date;
  }[]; // Array de empleados con información detallada
  createdAt: Date;
  updatedAt: Date;
  upgradeHistory?: {
    fromPlan: string;
    toPlan: string;
    date: Date;
    reason: 'user_limit_reached' | 'manual_upgrade' | 'downgrade';
  }[];
  config?: {
    timezone?: string;
    currency?: string;
    language?: string;
  };
}

// Nueva interfaz para planes
export interface Plan {
  id: 'individual' | 'business' | 'enterprise';
  name: string;
  price: string;
  maxUsers: number | null; // null = ilimitado
  features: string[];
  description: string;
  isPopular?: boolean;
}

// Interfaz para el modal de upgrade
export interface UpgradeOption {
  planId: 'business' | 'enterprise';
  name: string;
  price: string;
  maxUsers: number | null;
  features: string[];
  currentPlan?: boolean;
}

export interface KPI {
  fecha: Date;
  litrosVendidos: number;
  cobranzasTotal: number;
  porcentajeMora: number;
  clientesAtendidos: number;
  entregasRealizadas: number;
}

export interface RutaOptimizada {
  id: string;
  fecha: Date;
  clientes: {
    clienteId: string;
    orden: number;
    distanciaAlSiguiente: number;
    tiempoEstimado: number;
  }[];
  distanciaTotal: number;
  tiempoEstimadoTotal: number;
  zonas: string[];
  ubicacionInicial?: LatLng;
}

export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

export interface Visita {
  id: string;
  clienteId: string;
  soderoId: string; // ✅ Agregar campo soderoId
  fecha: FirestoreTimestamp;
  completada: boolean;
  notas?: string;
  productosEntregados?: {
    producto: string;
    cantidad: number;
  }[];
  tiempoVisita?: number; // en minutos
  ubicacionCompletado?: {
    lat: number;
    lng: number;
  };
}

export interface ClienteConRuta extends Cliente {
  coords?: LatLng;
  orden?: number;
  distanciaAlSiguiente?: number;
  tiempoEstimado?: number;
  notas?: string;
  estado?: EstadoVisita;
  ultimaVisita?: Date;
  zona?: string;
}

export interface NotificacionCliente {
  id: string;
  clienteId: string;
  tipo: 'llegada' | 'retraso' | 'cancelacion';
  mensaje: string;
  horaEstimada?: Date;
  estado: 'pendiente' | 'enviada' | 'entregada' | 'fallida';
  fechaCreacion: Date;
  fechaActualizacion: Date;
}

// Enums para estados y tipos
export enum EstadoVisita {
  PENDIENTE = 'pendiente',
  COMPLETADA = 'completado',
  CANCELADA = 'cancelado'
}

export enum TipoNotificacion {
  LLEGADA = 'llegada',
  RETRASO = 'retraso',
  CANCELACION = 'cancelacion'
}

export enum PrioridadCliente {
  BAJA = 'baja',
  MEDIA = 'media',
  ALTA = 'alta'
}

// Interfaces para configuración
export interface ConfiguracionRuta {
  horaInicio: string; // formato HH:mm
  horaFin: string; // formato HH:mm
  tiempoPromedioVisita: number; // en minutos
  descansos: {
    hora: string;
    duracion: number; // en minutos
  }[];
  zonaPredeterminada?: string;
  optimizacionPredeterminada: 'distancia' | 'tiempo' | 'mixta';
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RouteStats {
  distanciaTotal: number;
  tiempoTotal: number;
  distanciasIndividuales: number[];
  tiemposIndividuales: number[];
}

// ============================================
// NUEVO SISTEMA DE INVENTARIO DINÁMICO
// ============================================

export interface Producto {
  id: string;
  nombre: string;
  descripcion?: string;
  categoria: string;
  codigo?: string;
  codigoBarras?: string;
  unidadMedida: UnidadMedida;
  precioCompra: number;
  precioVenta: number;
  stock: number;
  stockMinimo: number;
  stockMaximo?: number;
  activo: boolean;
  proveedor?: string;
  fechaVencimiento?: Date;
  ubicacion?: string;
  imagen?: string;
  peso?: number; // en gramos
  volumen?: number; // en ml
  // Información del envase asociado (sistema simplificado)
  envaseRetornable?: boolean; // true = retornable, false = no retornable
  envaseCapacidadLitros?: number | null; // Capacidad del envase en litros (solo si es retornable)
  envaseNombreCompleto?: string | null; // Nombre completo del envase (solo si es retornable)
  envaseTipoId?: string; // ID del tipo de envase (para cálculos automáticos del backend)
  
  // Campos legacy (mantener para compatibilidad)
  envaseTipo?: 'comodato' | 'retornable' | 'descartable'; // Tipo de envase (legacy)
  envaseCapacidad?: number; // Capacidad del envase en litros (legacy)
  envaseNombre?: string; // Nombre del envase (legacy)
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

export interface CategoriaProducto {
  id: string;
  nombre: string;
  descripcion?: string;
  color: string;
  icono: string;
  activa: boolean;
  orden: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface MovimientoInventario {
  id: string;
  productoId: string;
  tipo: TipoMovimiento;
  cantidad: number;
  cantidadAnterior: number;
  cantidadNueva: number;
  motivo: string;
  referencia?: string; // ID de entrega, compra, ajuste, etc.
  observaciones?: string;
  fecha: Date;
  usuario: string;
  ubicacion?: string;
  createdAt: Date;
}

export interface AjusteInventario {
  id: string;
  fecha: Date;
  motivo: string;
  observaciones?: string;
  usuario: string;
  productos: {
    productoId: string;
    cantidadAnterior: number;
    cantidadNueva: number;
    diferencia: number;
    motivo?: string;
  }[];
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  aprobadoPor?: string;
  fechaAprobacion?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Proveedor {
  id: string;
  nombre: string;
  contacto: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  activo: boolean;
  productos: string[]; // IDs de productos
  createdAt: Date;
  updatedAt: Date;
}

export interface HistorialPrecio {
  id: string;
  productoId: string;
  precioAnterior: number;
  precioNuevo: number;
  tipo: 'compra' | 'venta';
  motivo: string;
  fecha: Date;
  usuario: string;
  createdAt: Date;
}

// Nuevo inventario vehicular dinámico
export interface InventarioVehiculoDinamico {
  id: string;
  fecha: Date;
  productos: {
    productoId: string;
    cantidad: number;
  }[];
  ubicacion?: string;
  conductor: string;
  observaciones?: string;
  updatedAt: Date;
  createdAt: Date;
}

// Interfaces para reportes
export interface ReporteInventario {
  fecha: Date;
  valorTotalInventario: number;
  productosConStockBajo: number;
  productosAgotados: number;
  movimientosDelDia: number;
  ventasDelDia: number;
  comprasDelDia: number;
  productos: {
    id: string;
    nombre: string;
    categoria: string;
    stock: number;
    stockMinimo: number;
    valor: number;
    rotacion: number;
    diasSinMovimiento: number;
  }[];
}

export interface MetricasInventario {
  valorTotal: number;
  cantidadProductos: number; // Total incluyendo inactivos
  productosActivos: number;
  productosConStockBajo: number; // Incluye activos e inactivos
  productosAgotados: number; // Incluye activos e inactivos
  categorias: number;
  movimientosHoy: number;
  ventasHoy: number;
}

// Enums
export enum TipoMovimiento {
  ENTRADA = 'entrada',
  SALIDA = 'salida',
  AJUSTE = 'ajuste',
  VENTA = 'venta',
  DEVOLUCION = 'devolucion',
  MERMA = 'merma',
  TRANSFERENCIA = 'transferencia',
  INICIAL = 'inicial'
}

export enum UnidadMedida {
  UNIDAD = 'unidad',
  LITRO = 'litro',
  KILOGRAMO = 'kilogramo',
  GRAMO = 'gramo',
  METRO = 'metro',
  CENTIMETRO = 'centimetro',
  CAJA = 'caja',
  PAQUETE = 'paquete',
  DOCENA = 'docena'
}

export enum EstadoProducto {
  ACTIVO = 'activo',
  INACTIVO = 'inactivo',
  DESCONTINUADO = 'descontinuado'
}

// Configuración del sistema de inventario
export interface ConfiguracionInventario {
  alertasStockBajo: boolean;
  alertasStockAgotado: boolean;
  alertasVencimiento: boolean;
  diasAlertaVencimiento: number;
  permitirVentaSinStock: boolean;
  actualizarPreciosAutomatico: boolean;
  margenGananciaDefault: number;
  moneda: string;
  codigoBarrasAutomatico: boolean;
  ubicacionesMultiples: boolean;
  manejarLotes: boolean;
  manejarVencimientos: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Filtros para búsquedas y reportes
export interface FiltrosProductos {
  categoria?: string;
  proveedor?: string;
  activo?: boolean;
  conStock?: boolean;
  stockBajo?: boolean;
  busqueda?: string;
  fechaDesde?: Date;
  fechaHasta?: Date;
  envaseRetornable?: boolean | 'todos'; // Filtro por tipo de envase: true = retornable, false = descartable, 'todos' = todos
}

export interface PaginacionProductos {
  pagina: number;
  limite: number;
  ordenarPor?: 'nombre' | 'precioVenta' | 'stock' | 'createdAt' | 'updatedAt';
  orden?: 'asc' | 'desc';
}

export interface ResultadoPaginado<T> {
  datos: T[];
  paginacion: {
    paginaActual: number;
    totalPaginas: number;
    totalElementos: number;
    limite: number;
    tieneSiguiente: boolean;
    tieneAnterior: boolean;
  };
}

export interface FiltrosMovimientos {
  productoId?: string;
  tipo?: TipoMovimiento;
  usuario?: string;
  fechaDesde?: Date;
  fechaHasta?: Date;
  referencia?: string;
}

export interface ValidacionStock {
  productoId: string;
  stockActual: number;
  stockMinimo: number;
  stockMaximo?: number;
  cantidadMovimiento: number;
  tipoMovimiento: TipoMovimiento;
}

export interface ResultadoTransaccion {
  exito: boolean;
  movimientoId?: string;
  stockAnterior: number;
  stockNuevo: number;
  mensaje: string;
  errores?: string[];
}

// Interfaz para la entrega actualizada con productos dinámicos
export interface EntregaDinamica {
  id: string;
  clienteId: string;
  fecha: Date;
  productos: {
    productoId: string;
    cantidad: number;
    precioUnitario: number;
    subtotal: number;
  }[];
  total: number;
  pagado: boolean;
  medioPago?: 'efectivo' | 'transferencia' | 'tarjeta';
  firmaURL?: string;
  fotoEntregaURL?: string;
  observaciones?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// SISTEMA DE CONTROL DE EQUIPOS
// ============================================

export interface Equipo {
  id: string;
  clienteId?: string; // ← HACER OPCIONAL para permitir equipos en inventario
  tipo: TipoEquipo;
  marca?: string; // Campo opcional
  modelo: string;
  numeroSerie: string;
  fechaInstalacion?: Date; // ← HACER OPCIONAL (solo cuando se asigna a cliente)
  estado: EstadoEquipo;
  observaciones?: string;
  fechaUltimoMantenimiento?: Date;
  proximoMantenimiento?: Date;
  costoInstalacion?: number;
  garantiaHasta?: Date;
  ubicacion?: string; // Ubicación específica en el cliente (ej: "Oficina principal", "Cocina")
  fotos?: string[]; // Array de URLs de fotos en base64
  
  // NUEVOS CAMPOS PARA INVENTARIO
  enInventario?: boolean; // true = en inventario, false = asignado a cliente
  ubicacionInventario?: string; // "Depósito", "Vehículo", "Almacén", etc.
  fechaIngresoInventario?: Date; // Fecha cuando ingresó al inventario
  
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

// Interfaz optimizada para mostrar información en cards móviles
export interface EquipoCardData {
  id: string;
  marca?: string; // Campo opcional
  modelo: string;
  tipo: TipoEquipo;
  estado: EstadoEquipo;
  fechaInstalacion: Date;
  ubicacion?: string;
  garantiaHasta?: Date;
  proximoMantenimiento?: Date;
}

// ============================================================================
// TIPOS PARA INVENTARIO DE EQUIPOS
// ============================================================================

/**
 * Filtros para búsqueda de equipos en inventario
 */
export interface FiltrosEquiposInventario {
  tipo?: TipoEquipo;
  marca?: string;
  estado?: EstadoEquipo;
  ubicacionInventario?: string;
  ubicacion?: string;
  clienteId?: string;
  enInventario?: boolean;
  necesitaMantenimiento?: boolean;
  busqueda?: string;
  fechaIngresoDesde?: Date;
  fechaIngresoHasta?: Date;
}

/**
 * Métricas de inventario de equipos
 */
export interface MetricasEquiposInventario {
  totalEquipos: number;
  equiposEnInventario: number;
  equiposAsignados: number;
  equiposPorTipo: Record<TipoEquipo, number>;
  equiposPorEstado: Record<EstadoEquipo, number>;
  equiposPorUbicacion: Record<string, number>;
  equiposDisponibles: number;
  equiposEnMantenimiento: number;
  equiposRetirados: number;
}

/**
 * Datos para crear equipo en inventario
 */
export interface EquipoInventarioForm {
  tipo: TipoEquipo;
  marca?: string; // Campo opcional
  modelo: string;
  numeroSerie: string;
  estado: EstadoEquipo;
  ubicacionInventario: string;
  observaciones?: string;
  costoInstalacion?: number;
  garantiaHasta?: Date;
  fotos?: string[];
}

export interface HistorialEquipo {
  id: string;
  equipoId: string;
  clienteId: string;
  tipoAccion: TipoAccionEquipo;
  fecha: Date;
  descripcion: string;
  observaciones?: string;
  costo?: number;
  tecnico?: string;
  usuario?: string; // Usuario que realizó la acción
  fotos?: string[]; // URLs de fotos del trabajo realizado
  createdAt: Date;
  createdBy: string;
}

export interface MantenimientoEquipo {
  id: string;
  equipoId: string;
  clienteId: string;
  tipo: TipoMantenimiento;
  fechaProgramada: Date;
  fechaRealizada?: Date;
  estado: EstadoMantenimiento;
  descripcion: string;
  observaciones?: string;
  costo?: number;
  tecnico?: string;
  tiempoRealizado?: number; // Tiempo en minutos que tomó realizar el mantenimiento
  calificacion?: number; // Calificación de 1-5 estrellas del servicio
  fotosAntes?: string[];
  fotosDespues?: string[];
  repuestosUtilizados?: {
    nombre: string;
    cantidad: number;
    costo: number;
  }[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

// Enums para equipos
export enum TipoEquipo {
  DISPENSER_NORMAL = 'dispenser_normal',
  DISPENSER_FRIO_CALOR = 'dispenser_frio_calor',
  MAQUINA_AGUA = 'maquina_agua',
  PURIFICADOR = 'purificador',
  ENFRIADOR = 'enfriador',
  OTRO = 'otro'
}

export enum EstadoEquipo {
  ACTIVO = 'activo',
  MANTENIMIENTO = 'mantenimiento',
  RETIRADO = 'retirado',
  REPARACION = 'reparacion',
  GARANTIA = 'garantia'
}

export enum TipoAccionEquipo {
  INSTALACION = 'instalacion',
  RETIRO = 'retiro',
  MANTENIMIENTO = 'mantenimiento',
  REPARACION = 'reparacion',
  REEMPLAZO = 'reemplazo',
  INSPECCION = 'inspeccion',
  OTRO = 'otro'
}

export enum TipoMantenimiento {
  PREVENTIVO = 'preventivo',
  CORRECTIVO = 'correctivo',
  EMERGENCIA = 'emergencia',
  INSPECCION = 'inspeccion'
}

export enum EstadoMantenimiento {
  PROGRAMADO = 'programado',
  EN_PROCESO = 'en_proceso',
  COMPLETADO = 'completado',
  CANCELADO = 'cancelado',
  VENCIDO = 'vencido'
}

// Filtros para equipos
export interface FiltrosEquipos {
  clienteId?: string;
  tipo?: TipoEquipo;
  estado?: EstadoEquipo;
  marca?: string;
  fechaInstalacionDesde?: Date;
  fechaInstalacionHasta?: Date;
  necesitaMantenimiento?: boolean;
  busqueda?: string;
}

// Métricas de equipos
export interface MetricasEquipos {
  totalEquipos: number;
  equiposActivos: number;
  equiposEnMantenimiento: number;
  equiposRetirados: number;
  mantenimientosPendientes: number;
  mantenimientosVencidos: number;
  equiposPorTipo: Record<TipoEquipo, number>;
  equiposPorEstado: Record<EstadoEquipo, number>;
  costoTotalMantenimientos: number;
  equiposConGarantia: number;
}

// ============================================
// EXPORTAR TIPOS DE COMODATO
// ============================================

// Re-exportar todos los tipos de comodato para facilitar las importaciones
export * from './comodatoTypes';